import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { RealtimeProvider } from "@/context/Realtime";
import AppShell from "@/components/AppShell";
import AuthPage from "@/pages/AuthPage";
import UserHome from "@/pages/user/UserHome";
import UserJobs from "@/pages/user/UserJobs";
import JobForm from "@/pages/user/JobForm";
import WorkerPublic from "@/pages/user/WorkerPublic";
import JobDetail from "@/pages/JobDetail";
import CoinPage from "@/pages/CoinPage";
import { ChatList, ChatRoom } from "@/pages/Chat";
import Profile from "@/pages/Profile";
import { EditProfile, KycPage, WorkerRegister, SkillsPage, CertificatesPage, PortfolioPage, SchedulePage, PayoutPage, PasswordPage } from "@/pages/ProfilePages";
import WorkerHome from "@/pages/worker/WorkerHome";
import WorkerHistory from "@/pages/worker/WorkerHistory";
import { AdminLayout, AdminDashboard, AdminUsers, AdminFinance, AdminKyc, AdminAccount, AdminSettings, AdminCategories } from "@/pages/admin/Admin";
import { IdCardPage, MarketplacePage, SupportPage } from "@/pages/WorkerExtras";
import AdminPush from "@/pages/admin/AdminPush";

function Guard({ admin, children }) {
  const { user } = useAuth();
  if (user === null) return <div className="min-h-screen grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-emerald-600" /></div>;
  if (!user) return <Navigate to="/login" replace />;
  if (admin && user.role !== "admin") return <Navigate to="/" replace />;
  if (!admin && user.role === "admin") return <Navigate to="/admin" replace />;
  return children;
}

function GuestOnly({ children }) {
  const { user } = useAuth();
  if (user) return <Navigate to={user.role === "admin" ? "/admin" : "/"} replace />;
  return children;
}

function ModeSwitch({ user: U, worker: W }) {
  const { mode } = useAuth();
  return mode === "worker" ? <W /> : <U />;
}

export default function App() {
  return (
    <AuthProvider>
      <RealtimeProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<GuestOnly><AuthPage mode="login" /></GuestOnly>} />
          <Route path="/register" element={<GuestOnly><AuthPage mode="register" /></GuestOnly>} />
          <Route path="/admin" element={<Guard admin><AdminLayout /></Guard>}>
            <Route index element={<AdminDashboard />} />
            <Route path="users" element={<AdminUsers />} />
            <Route path="finance" element={<AdminFinance />} />
            <Route path="kyc" element={<AdminKyc />} />
            <Route path="categories" element={<AdminCategories />} />
            <Route path="push" element={<AdminPush />} />
            <Route path="account" element={<AdminAccount />} />
            <Route path="settings" element={<AdminSettings />} />
          </Route>
          <Route element={<Guard><AppShell /></Guard>}>
            <Route path="/" element={<ModeSwitch user={UserHome} worker={WorkerHome} />} />
            <Route path="/jobs" element={<ModeSwitch user={UserJobs} worker={WorkerHistory} />} />
            <Route path="/jobs/new" element={<JobForm />} />
            <Route path="/jobs/:id" element={<JobDetail />} />
            <Route path="/workers/:id" element={<WorkerPublic />} />
            <Route path="/coins" element={<CoinPage />} />
            <Route path="/chat" element={<ChatList />} />
            <Route path="/chat/:jobId" element={<ChatRoom />} />
            <Route path="/profile" element={<Profile />} />
            <Route path="/profile/edit" element={<EditProfile />} />
            <Route path="/profile/kyc" element={<KycPage />} />
            <Route path="/profile/worker-register" element={<WorkerRegister />} />
            <Route path="/profile/skills" element={<SkillsPage />} />
            <Route path="/profile/certificates" element={<CertificatesPage />} />
            <Route path="/profile/portfolio" element={<PortfolioPage />} />
            <Route path="/profile/schedule" element={<SchedulePage />} />
            <Route path="/profile/payout" element={<PayoutPage />} />
            <Route path="/profile/password" element={<PasswordPage />} />
            <Route path="/profile/id-card" element={<IdCardPage />} />
            <Route path="/profile/marketplace" element={<MarketplacePage />} />
            <Route path="/profile/support" element={<SupportPage />} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-center" richColors />
      </RealtimeProvider>
    </AuthProvider>
  );
}
